import { useCallback, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useChannelSettingsContext } from '@sendbird/uikit-react/ChannelSettings/context';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslate } from 'ra-core';
import { useToggleOperatorsStatus } from '../../../api/hooks/sendbird/useToggleOperatorsStatus';
import { useGetUsers, type UserDto } from '../../../hooks/useGetUsers';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { ChannelSettingsModal } from './ChannelSettingsModal';
import { ChannelUserCard } from '../ChannelUserCard';
import { SearchTextInput } from '../SearchTextInput';
import { UserInfiniteList } from '../UserInfiniteList';

export interface AddOperatorsModalProps {
  open: boolean;
  onCancel(): void;
  onSubmit(): void;
}

export function AddOperatorsModal({
  onCancel,
  onSubmit,
  open,
}: AddOperatorsModalProps) {
  const translate = useTranslate();
  const { channel } = useChannelSettingsContext();
  const [selectedUsers, setSelectedUsers] = useState<UserDto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: users,
    isLoading,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage,
    error,
    isError,
    isFetchNextPageError,
    isRefetching,
  } = useInfiniteQuery(
    useGetUsers({
      query: searchTerm,
      memberOfChannel: channel.url,
      notOperatorInChannel: channel.url,
    })
  );

  const flattenedUsers = useMemo(() => {
    if (!users?.pages) return [];

    return users.pages
      .flatMap((page) => page.data)
      .map((user) => ({ ...user, userId: user.id.toString() }));
  }, [users]);

  const {
    mutateAsync: toggleOperatorsStatusAsync,
    isPending: isAddingOperators,
  } = useToggleOperatorsStatus();

  const handleAddOperators = useCallback(async () => {
    const userIdsToAdd = selectedUsers.map((user) => `${user.id}`);

    await toggleOperatorsStatusAsync({
      channelUrl: channel.url,
      userIds: userIdsToAdd,
      isOperator: true,
    });

    setSelectedUsers([]);
    onSubmit();
  }, [selectedUsers, toggleOperatorsStatusAsync, channel.url, onSubmit]);

  return (
    <ChannelSettingsModal
      open={open}
      onCancel={onCancel}
      title={translate('chat.addOperatorsModalTitle')}
      header={
        <>
          <SearchTextInput
            label={translate('chat.searchMembers')}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
          <p className="text-xs text-muted-foreground">
            {translate('chat.selectedCount', { count: selectedUsers.length })}
          </p>
        </>
      }
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {translate('chat.close')}
          </Button>
          <Button
            type="button"
            onClick={() => void handleAddOperators()}
            disabled={selectedUsers.length === 0 || isAddingOperators}
          >
            {isAddingOperators && <Loader2 className="animate-spin" />}
            {isAddingOperators
              ? translate('chat.adding')
              : translate('chat.add')}
          </Button>
        </>
      }
    >
      <UserInfiniteList
        users={flattenedUsers}
        isLoadingUsers={isFetchingNextPage}
        isPendingUsers={isLoading}
        refetchUsers={refetch}
        fetchNextPageUsers={fetchNextPage}
        errorUsers={error}
        isErrorUsers={isError}
        isFetchNextPageError={isFetchNextPageError}
        isRefetching={isRefetching}
        hasNextPageUsers={hasNextPage}
        renderUser={(user) => {
          const isSelected = selectedUsers.some(
            (selectedUser) => selectedUser.id === user.id
          );

          return (
            <ChannelUserCard
              user={user}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              right={
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedUsers((previous) => [...previous, user]);
                    } else {
                      setSelectedUsers((previous) =>
                        previous.filter(
                          (selectedUser) => selectedUser.id !== user.id
                        )
                      );
                    }
                  }}
                  aria-label={translate('chat.selectUser', {
                    name: user.displayName,
                  })}
                />
              }
            />
          );
        }}
      />
    </ChannelSettingsModal>
  );
}
